import { useMemo, useState } from 'react';
import { useCreatePropinsi, useDeletePropinsi, usePropinsiList, useUpdatePropinsi } from '@/hooks/usePropinsi';
import { Propinsi } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';

const emptyForm: Partial<Propinsi> = {
  nama: '',
};

export function ProvinsiList() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<Propinsi>>(emptyForm);

  const { data, isLoading } = usePropinsiList({ page, limit: 10, search });
  const createPropinsi = useCreatePropinsi();
  const updatePropinsi = useUpdatePropinsi();
  const deletePropinsi = useDeletePropinsi();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const totalPage = useMemo(() => Math.max(1, Math.ceil((data?.total || 0) / 10)), [data?.total]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (item: Propinsi) => {
    setEditingId(item.id);
    setForm({ nama: item.nama || '' });
    setOpen(true);
  };

  const submitForm = async () => {
    if (!form.nama?.trim()) return;

    if (editingId) {
      await updatePropinsi.mutateAsync({ id: editingId, data: form });
    } else {
      await createPropinsi.mutateAsync(form);
    }

    setOpen(false);
  };

  const submitDelete = async () => {
    if (!pendingDeleteIds.length) return;
    for (const id of pendingDeleteIds) {
      await deletePropinsi.mutateAsync(id);
    }
    selection.clearSelection();
    setPendingDeleteIds([]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Provinsi</h2>
          <p className="text-muted-foreground">Kelola data provinsi yang tersimpan di database.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Tambah Provinsi
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cari nama / kode provinsi..."
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              className="pl-8"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daftar Provinsi</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="provinsi"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deletePropinsi.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Jumlah Kota</TableHead>
                <TableHead className="w-28">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center">Memuat...</TableCell>
                </TableRow>
              ) : data?.data.length ? (
                data.data.map((item) => (
                  <TableRow key={item.id} className={selection.isSelected(item.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(item.id)} onCheckedChange={(checked) => selection.toggleOne(item.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell className="font-medium">{item.nama || '-'}</TableCell>
                    <TableCell>{item.kotaCount ?? 0}</TableCell>
                    <TableCell className="space-x-2">
                      <Button variant="outline" size="icon" onClick={() => openEdit(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => setPendingDeleteIds([item.id])}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center">Belum ada data provinsi.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Sebelumnya
        </Button>
        <span className="text-sm text-muted-foreground">Halaman {page} dari {totalPage}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPage} onClick={() => setPage((p) => p + 1)}>
          Selanjutnya
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Ubah Provinsi' : 'Buat Provinsi'}</DialogTitle>
            <DialogDescription>Perubahan akan langsung tersimpan ke database.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Nama provinsi"
              value={form.nama || ''}
              onChange={(e) => setForm((v) => ({ ...v, nama: e.target.value }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submitForm}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hapus Provinsi</DialogTitle>
            <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>Batal</Button>
            <Button variant="destructive" onClick={submitDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
